/**
 * Data layer for `custom-login-domain-for-publisher-portal`. Thin typed fetch wrappers over /api/v1/custom-login-domain-for-publisher-portal.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CustomLoginDomainForPublisherPortalRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCustomLoginDomainForPublisherPortalResponse {
	count: number;
	items: CustomLoginDomainForPublisherPortalRow[];
}

export interface CreateCustomLoginDomainForPublisherPortalBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/custom-login-domain-for-publisher-portal";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCustomLoginDomainForPublisherPortalResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCustomLoginDomainForPublisherPortalResponse>(res);
}

export async function create(body: CreateCustomLoginDomainForPublisherPortalBody): Promise<CustomLoginDomainForPublisherPortalRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CustomLoginDomainForPublisherPortalRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const customLoginDomainForPublisherPortalQueryKeys = {
	all: ["custom-login-domain-for-publisher-portal"] as const,
	list: () => [...customLoginDomainForPublisherPortalQueryKeys.all] as const,
	detail: (id: string) => [...customLoginDomainForPublisherPortalQueryKeys.all, id] as const,
};
